import { useEffect, useState } from "react";
import { useUserData } from "../utils/UserData";
import { Link } from "react-router-dom";
import { AiFillStar } from "react-icons/ai";
import { BiGitRepoForked } from "react-icons/bi";
import { projectsMeta } from "../utils/projects.config";
import { usePageTitle } from "../utils/usePageTitle";

export default function PageGithub() {
    usePageTitle("What I've Been Building");
  const [repo, setRepo] = useState([]);
  const data = useUserData();

  useEffect(() => {
    fetch(data.apiToConsume.github)
      .then((res) => res.json())
      .then((res) => {
        setRepo(res);
      });
  },[data.apiToConsume.github]);

    // console.log(repo);

  const projects = repo
    .filter((r) => {
      const meta = projectsMeta[r.name];
      if (meta?.hidden) return false;
      if (r.svn_url === "https://github.com/sarahcssiqueira/sarahcssiqueira") return false;
      if (r.fork) return false;
      return true;
    })
    .map((r) => ({
      ...r,
      ...(projectsMeta[r.name] || {}),
      // Use custom description if available, else fall back to GitHub's
      description: projectsMeta[r.name]?.description || r.description,
      // Use custom displayName if available, else format the repo name
      displayName:
        projectsMeta[r.name]?.displayName ||
        r.name.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    }))
    .sort((a, b) => {
      // Featured projects first, then by star count
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return b.stargazers_count - a.stargazers_count;
    });

  return (
    <main className="max-w-6xl mx-auto px-6 py-12 text-slate-800 font-sans space-y-10">
      <div className="space-y-3">
        <h2 className="text-3xl font-semibold text-center text-slate-800">
          What I've Been Building
        </h2>
        <p className="text-base leading-relaxed text-slate-500 text-center max-w-2xl mx-auto">
          {data.gitHubPageDesc}
        </p>
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <article
            key={project.id}
            className="flex flex-col bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-5 gap-3"
          >
            {/* 1. Name — first thing you read */}
            <Link
              to={project.svn_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-bold text-slate-800 hover:text-purple-700 transition-colors leading-snug"
            >
              {project.displayName}
            </Link>

            {/* 2. Stack — highlighted, right below the name */}
            {Array.isArray(project.stack) && project.stack.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {project.stack.map((tech, i) => (
                  <li
                    key={i}
                    className="text-xs font-semibold bg-purple-100 text-purple-800 px-2.5 py-0.5 rounded-full"
                  >
                    {tech}
                  </li>
                ))}
              </ul>
            )}

            {/* 3. Description */}
            <p className="text-sm text-slate-600 leading-relaxed flex-1">
              {project.description}
            </p>

            {/* 4. Extra links */}
            <div className="flex flex-wrap gap-3 text-sm">
              {project.articleUrl && (
                <Link
                  to={project.articleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-500 font-medium"
                >
                  Read the article →
                </Link>
              )}
              {project.liveUrl && (
                <Link
                  to={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-500 font-medium"
                >
                  View live →
                </Link>
              )}
            </div>

            {/* 5. Footer: stars/forks + GitHub link */}
            <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <AiFillStar className="text-purple-400" />
                  {project.stargazers_count}
                </span>
                <span className="flex items-center gap-1">
                  <BiGitRepoForked className="text-purple-400" />
                  {project.forks_count}
                </span>
              </div>

              <Link
                to={project.svn_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-purple-700 hover:text-purple-500 transition-colors"
              >
                View on GitHub ↗
              </Link>
            </div>

            {/* 6. Hashtags — last, decorative */}
            {Array.isArray(project.topics) && project.topics.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {project.topics.map((topic, i) => (
                  <li key={i} className="text-xs text-slate-400">
                    #{topic.replace(/-/g, " ")}
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </section>
    </main>
  );
}
